import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTopButton from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PageMeta from '@/components/feature/PageMeta';
import ComicCard from '@/components/feature/ComicCard';
import { categories, getComicsByCategory, getCategoryBySlug } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'romance');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeSlug]);

  const currentCategory = getCategoryBySlug(activeSlug);
  const comics: Comic[] = getComicsByCategory(activeSlug);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory?.name || '漫画分类'} - 韩漫之家`,
    description: `韩漫之家为您提供${currentCategory?.name || ''}类韩国漫画在线阅读，包含${comics.length}部精选漫画，持续更新中。`,
    url: `https://hanmanzhijia.com/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫之家',
      url: 'https://hanmanzhijia.com',
    },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: comics.map((comic, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'ComicStory',
          name: comic.title,
          author: { '@type': 'Person', name: comic.author },
          url: `https://hanmanzhijia.com/article/${comic.id}`,
        },
      })),
    },
  };

  const desc = currentCategory
    ? `韩漫之家为您提供${currentCategory.name}类韩国漫画在线阅读，共收录${currentCategory.count}部漫画，持续更新中。`
    : '韩漫之家分类浏览，韩国漫画在线阅读。';

  return (
    <>
      <PageMeta
        title={`${currentCategory?.name || '漫画分类'} - 韩漫之家 - 免费韩国漫画在线阅读`}
        description={desc}
        keywords={`韩漫之家,${currentCategory?.name || ''},韩国漫画,免费漫画,在线阅读`}
        canonicalUrl={`https://hanmanzhijia.com/category/${activeSlug}`}
        schema={schema}
      />
      <ScrollToTopButton />
      <Navbar />

      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="py-4">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '分类浏览', href: '/category/romance' },
                { label: currentCategory?.name || '' },
              ]}
            />
          </div>

          {/* Page Header */}
          <div className="py-6 border-b border-background-200">
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
              {currentCategory?.name || '漫画分类'}
            </h1>
            <p className="mt-2 text-sm text-foreground-500">
              共 {currentCategory?.count || comics.length} 部漫画 · 发现你喜欢的韩国漫画
            </p>
          </div>

          {/* Category Tabs */}
          <div className="py-6 overflow-x-auto">
            <div className="flex flex-wrap items-center gap-2 min-w-max">
              {categories.map(cat => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                  }`}
                >
                  {cat.name}
                  <span className="ml-1 text-xs opacity-70">({cat.count})</span>
                </Link>
              ))}
            </div>
          </div>

          {/* Comic Grid */}
          <section className="py-6" aria-labelledby="cat-section-title">
            <h2 id="cat-section-title" className="sr-only">
              {currentCategory?.name}漫画列表
            </h2>
            {comics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-product-shop>
                {comics.map(comic => (
                  <ComicCard
                    key={comic.id}
                    id={comic.id}
                    title={comic.title}
                    author={comic.author}
                    cover={comic.cover}
                    rating={comic.rating}
                    views={comic.views}
                    tags={comic.tags}
                    status={comic.status}
                    categoryName={comic.categoryName}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                  <i className="ri-emotion-sad-line text-2xl w-8 h-8 flex items-center justify-center"></i>
                </div>
                <p className="text-foreground-500">该分类暂无漫画，看看其他分类吧</p>
              </div>
            )}
          </section>

          {/* Bottom Nav */}
          <div className="py-8 text-center">
            <Link
              to="/"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line mr-1 w-4 h-4 inline-flex items-center justify-center"></i>
              返回首页
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}